import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { ElementRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ProductCard } from './ProductCard';
import { ProductFilters } from './ProductFilters';

import { AnimateSpin } from '@/components/AnimateSpin';
import { useGetMerchandisingCollectionSliceProducts } from '@/hooks/useGetMerchandisingCollectionSliceProducts';
import { useReorderMerchandisingCollectionSliceProduct } from '@/hooks/useReorderMerchandisingCollectionSliceProduct';
import { MerchandisingCollectionSlice } from '@/services/MerchandisingCollectionsService';

interface SliceProductsProps {
  slice: MerchandisingCollectionSlice;
}
export function SliceProducts({ slice }: SliceProductsProps) {
  const [isSaving, setIsSaving] = useState(false);

  const endProductsRef = useRef<ElementRef<'li'>>(null);

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Slice.components.SliceProducts',
  });

  const {
    sliceProducts,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    totalItems,
  } = useGetMerchandisingCollectionSliceProducts(slice.collectionId, slice.id);

  const { reorderSliceProduct, isLoading: isReordering } =
    useReorderMerchandisingCollectionSliceProduct();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    try {
      if (typeof active.id === 'string' && typeof over?.id === 'string') {
        await reorderSliceProduct({
          collectionId: slice.collectionId,
          sliceId: slice.id,
          dto: { products: [active.id, over.id] },
        });
      }
      toast.success(t('toast.success'));
    } catch {
      toast.error(t('toast.error'));
    }
  }

  useEffect(() => {
    if (!endProductsRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        const { isIntersecting } = entries[0];

        if (!hasNextPage) {
          obs.disconnect();
          return;
        }

        if (isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: '30px',
      },
    );

    observer.observe(endProductsRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {totalItems === 0 && t('noProductsFound')}

          {totalItems > 0 &&
            t('productsCount', {
              count: slice.productsCount,
              plural: slice.productsCount !== 1 ? 's' : '',
            })}
        </p>

        {sliceProducts.length > 0 && <ProductFilters slice={slice} />}
      </div>

      {sliceProducts.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sliceProducts.map((i) => i.productId)}
            strategy={rectSortingStrategy}
            disabled={isSaving || isReordering}
          >
            <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {sliceProducts.map((product) => (
                <li key={product.productId}>
                  <ProductCard
                    product={product}
                    isSaving={isSaving || isReordering}
                    onSaving={setIsSaving}
                  />
                </li>
              ))}

              <li ref={endProductsRef}>{isFetching && <AnimateSpin />}</li>
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
